export enum BulkImportAction {
  NEW = 'new',
  UPDATE = 'update',
  ERROR = 'error',
}

export interface Warehouse {
  id: number;
  code: string;
  name: string;
  level: string;
  provider: string;
}

export interface WarehouseSummary {
  warehouseId: number;
  totalFinancingAmount: number;
  loanCount: number;
}

// A loan's financing amount is a value that each change replaces (not a sum of entries).
export interface LoanDebtSummary {
  loanId: string;
  financingAmount: number;
  effectiveDate: string;
  lastComment: string;
  lastUpdatedBy: string;
  lastUpdatedAt: string;
}

// Header of the loan dialog; also pre-fills the Entry tab with the current amount.
export interface LoanDebtDetail {
  loanId: string;
  warehouse: Warehouse;
  financingAmount: number;
  effectiveDate: string;
  comment: string;
  lastUpdatedBy: string;
  lastUpdatedAt: string;
}

// One value a loan's financing amount had (History tab). change is null for the first value.
export interface FinancingAmountChange {
  historyId: number;
  // Warehouse the loan was in at that time (a loan can move while its amount is 0).
  warehouseName: string;
  financingAmount: number;
  change: number | null;
  effectiveDate: string;
  comment: string;
  source: string;
  recordedBy: string;
  recordedAt: string;
}

export interface FinancingAmountValues {
  financingAmount: number;
  effectiveDate: string;
  comment: string;
}

export interface SetFinancingAmountRequest extends FinancingAmountValues {
  warehouseId: number;
  loanIds: string[];
}

export interface SetFinancingAmountResponse {
  createdLoanIds: string[];
  updatedLoanIds: string[];
}

export interface BulkImportRow {
  rowNumber: number;
  warehouseId: number;
  warehouseName: string;
  loanId: string;
  amount: number;
  effectiveDate: string;
  comment: string;
  action: BulkImportAction;
  issues: string[];
  include?: boolean;
}

export interface BulkImportPreview {
  fileName: string;
  rows: BulkImportRow[];
  // Loan IDs of the file that don't exist or aren't funded; their rows are errors.
  notFoundLoanIds: string[];
}

export interface BulkImportResult {
  newLoans: number;
  loansUpdated: number;
}

// Effective dates travel to/from the API as plain dates.
export const API_DATE_FORMAT = 'yyyy-MM-dd';
export const DISPLAY_DATE_FORMAT = 'MMM d, yyyy';
export const DISPLAY_DATE_TIME_FORMAT = 'MMM d, yyyy · h:mm a';

export const LOANS_PAGE_SIZE = 50;
export const BULK_IMPORT_PREVIEW_PAGE_SIZE = 25;

export const BULK_IMPORT_FILE_TYPES = ['xlsx', 'xls', 'csv'];

// Header row of the bulk import spreadsheet (also the downloadable template). The server matches headers
// case/space-insensitively and accepts common aliases, so users can keep their existing spreadsheet headings.
export const BULK_IMPORT_COLUMNS = [
  { key: 'warehouse', label: 'Warehouse Line', optional: false },
  { key: 'loanId', label: 'Loan ID', optional: false },
  { key: 'amount', label: 'Financing Amount', optional: false },
  { key: 'effectiveDate', label: 'Effective Date', optional: true },
  { key: 'comment', label: 'Comment', optional: true },
];

export const BULK_IMPORT_TEMPLATE_FILE_NAME = 'debt-tracking-template.csv';

export const BULK_IMPORT_TEMPLATE_ROWS = [
  ['Bonsai', '85290', '125000', '04/15/2026', 'Balance after draw 4'],
  ['Bonsai', '85215', '98500.00', '04/15/2026', 'Corrected balance'],
  ['Chestnut', '92418', '80000', '04/16/2026', 'Balance after draw 2'],
  ['Juniper', '99001', '450000', '', 'New origination (date optional)'],
];
